import {initializeSupport, resetSupport} from '../Support/messenger';
import IDP_CONFIG from './idp-config';
import * as api from '@/rest/api';
import {confirmError, confirmSuccess} from '@/ui/Confirm';
import {setLoggingUser} from "@/lib/remoteLogging";
import {stopRecording} from '@/lib/sessionReplay';
import locals from '@/lib/localStorage';
import * as polling from '@/queues/polling';
import store from '@/store/store';
import * as MiscActions from '@/store/actions/miscActions';
import util from '@/lib/util';
import pWaitFor from 'p-wait-for';
import Alert from '@/lib/alert';
import {fetchFromServer} from "@/queues/processQueue";
import {
    getCurrentFirm,
    getCurrentFirmId,
    getCurrentUser,
    getCurrentUserId,
} from "@/lib/picos/currentLogin";
import {uuid} from "@/@types/schema";
import User from "@/entities/user";
import * as idb from "@/lib/idbd";
import {socket} from "@/lib/picos/socket";
import {globalAbortSignal} from "@/lib/picos/abortController";
import {purgeAutosaveOnce} from "@/lib/autosavePurge";

// Re-export role utilities for backward compatibility
import {
    LEGAL_ROLES, ALL_RELATED,
    getRoles, getMyRoles, hasRole, hasRoles, hasRoleId, hasLegalRole,
    isAdmin, isOwner, isUser, isInGroup,
    grantRole, grantRoleId, revokeRole, revokeRoleId
} from './roles';

const logging = locals.getBoolean(locals.AUTH_LOGGING, false);
const logFormat = "background: #fdebd0; padding: 2px; border-radius: 4px; color: black";

/**
 * Build a user-actionable login error message from an arbitrary error or
 * Result error. Empty messages and `Failed to fetch` get a friendly fallback
 * so the user is never shown a bare "Error Logging In" with no body.
 *
 * Used by `runPostLoginSequence` to populate `state.misc.authenticationError`,
 * which `Home.tsx` reads to render an inline error screen with Retry / Log Out
 * actions.
 */
/**
 * Build a user-actionable login error with a title that matches the failure.
 * Returns `{title, message}` for `state.misc.authenticationError`.
 */
function buildLoginError(err: any): {title: string; message: string} {
    const raw =
        (typeof err?.message === 'string' && err.message) ||
        (typeof err?.message?.clientMessage === 'string' && err.message.clientMessage) ||
        '';

    const status = typeof err?.status === 'number' ? err.status : 0;

    // Network-level failures — server down, DNS failure, CORS from a 502, etc.
    if (!raw || raw.includes('Failed to fetch')) {
        return {
            title: 'Server Unreachable',
            message: 'We could not reach the Outlaw Practice servers. Please check your internet connection and try again.',
        };
    }

    // HTTP 5xx from a server that responded but errored internally
    if (status >= 500) {
        return {
            title: 'Server Error',
            message: raw || 'The server encountered an unexpected error. Please try again in a moment.',
        };
    }

    // HTTP 4xx — likely auth/permission issue
    if (status >= 400) {
        return {
            title: 'Authentication Error',
            message: raw,
        };
    }

    // Anything else
    return {
        title: 'Login Error',
        message: raw,
    };
}

class Auth {
    // Delegate role utilities to roles.ts for backward compatibility
    static LEGAL_ROLES = LEGAL_ROLES;
    static ALL_RELATED = ALL_RELATED;
    static getRoles = getRoles;
    static getMyRoles = getMyRoles;
    static hasRole = hasRole;
    static hasRoles = hasRoles;
    static hasRoleId = hasRoleId;
    static hasLegalRole = hasLegalRole;
    static isAdmin = isAdmin;
    static isOwner = isOwner;
    static isUser = isUser;
    static isInGroup = isInGroup;
    static grantRole = grantRole;
    static grantRoleId = grantRoleId;
    static revokeRole = revokeRole;
    static revokeRoleId = revokeRoleId;

    static changeEmail = (userId: uuid, oldEmail: string, newEmail: string, cb: (user: User) => void) => {
        api.changeEmail(userId, oldEmail, newEmail)
            .then((user) => {
                cb(user);
            })
            .catch((err: any) => {
                confirmError('Error', err.message?.clientMessage);
                cb(null);
            });
    };

    /**
     * Trigger Auth0 password reset email.
     * Uses the Auth0 Authentication API directly (no auth0-js dependency).
     */
    static changePassword = (options: { email: string; connection: string }): void => {
        fetch(`https://${IDP_CONFIG.domain}/dbconnections/change_password`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                client_id: IDP_CONFIG.clientId,
                email: options.email,
                connection: options.connection,
            }),
        })
            .then(async (res) => {
                const text = await res.text();
                if (res.ok) confirmSuccess('Success', text);
                else confirmError('Error', text);
            })
            .catch((err) => {
                confirmError('Error', err.message);
            });
    };

    /**
     * Check if the user is authenticated by checking localStorage tokens.
     * This is the bridge method used by many files. The Auth0 SDK manages
     * tokens internally, but we also write them to localStorage for backward
     * compatibility with api.ts and other consumers.
     */
    static isAuthenticated = (): boolean => {
        const accessTokenPresent = !!localStorage.getItem(locals.AUTH_ACCESS_TOKEN);
        const expiresAt = locals.getNumeric(locals.AUTH_EXPIRES_AT, 0);
        const accessTokenValid = accessTokenPresent && (Date.now() < expiresAt);

        return !!accessTokenPresent && !!accessTokenValid;
    }

    /**
     * Run the post-login orchestration sequence.
     * Extracted from the old setSession() method. Called by usePostLoginOrchestration hook.
     *
     * This preserves the EXACT timing sequence from the original setSession():
     * 1. Extract app_metadata + user_metadata from ID token claims
     * 2. Dispatch setPortalUser
     * 3. Store firmId, fetch firmInfo
     * 4. Determine which firmId to use (multi-firm logic)
     * 5. Validate firmId and userId exist
     * 6. Portal path: finishSessionSetup() -> polling.start()
     * 7. Non-portal path: verifyLogin() -> subscription check -> finishSessionSetup() -> polling.start()
     * 8. finishSessionSetup() stores tokens, registers socket, dispatches loginResult, opens IDB
     * 9. Finally: clear loading, authenticated support initialization
     */
    static runPostLoginSequence = async (
        app_metadata: any,
        user_metadata: any,
        email: string,
        user_id: string,
        accessToken: string,
        idToken: string,
        expiresIn: number
    ) => {
        try {
            const userId = app_metadata.userId; // This is the contactId if a portal user
            const isPortal = !!user_metadata.portalUser;
            store.dispatch(MiscActions.setPortalUser(isPortal));

            locals.setString(locals.USER_FIRM_ID, app_metadata.firmId);

            // Store tokens early so api.ts can use them for firmInfo and other calls
            localStorage.setItem(locals.AUTH_ACCESS_TOKEN, accessToken);
            localStorage.setItem(locals.AUTH_ID_TOKEN, idToken);
            locals.setNumeric(locals.AUTH_TTL, expiresIn);
            locals.setNumeric(locals.AUTH_EXPIRES_AT, (expiresIn * 1000) + Date.now());

            if (store.getState().misc.isConnected) {
                try {
                    const firms = await api.firmInfo([app_metadata.firmId])
                    // firmInfo returns [] when the firmId isn't in the caller's JWT
                    // firms[] or isn't found in the DB. An empty array is truthy, so
                    // the old `firms[0].firm` threw "reading 'firm'" on undefined.
                    const firm = firms?.[0]?.firm;
                    if (firm) {
                        locals.setObject(locals.USER_FIRM, firm);
                    }
                }
                catch (err) {
                    // Don't pop a toast — if the server is unreachable the
                    // login error screen (Home.tsx) already shows the failure
                    // with Retry / Log Out actions. A technical toast on top
                    // of that is confusing and redundant.
                    console.warn('🔑 Auth: firmInfo failed during login, suppressing toast', err.message);
                }
            }

            locals.setObject(locals.PORTAL_FIRM, getCurrentFirm());

            setLoggingUser();

            let firmId: uuid;

            if (app_metadata.firms.length === 1) {
                firmId = app_metadata.firms[0];
            }
            else {
                const localFirm = getCurrentFirmId();

                if (localFirm) {
                    const match = app_metadata.firms.includes(localFirm);

                    if (match) {
                        firmId = localFirm;
                    }
                    else {
                        console.warn('🔑 Auth: Locally stored firmId is not in the approved list of firms.');
                        firmId = app_metadata.firms[0];
                    }
                }
                else {
                    firmId = app_metadata.firms[0];
                }
            }

            if (!firmId || !userId) {
                store.dispatch(MiscActions.loginResult(false, 'No app_metadata found', null, null));

                console.error('🔑 Auth: Neither App nor User Metadata fields returned from Auth0', {
                    firmId: firmId, userId: userId
                })
                throw new Error('Neither App nor User Metadata fields returned from authentication provider');
            }

            store.dispatch(MiscActions.setLoadingMessage('Check login...'));

            if (logging) console.debug(`%c🔑 Auth: Calling api.loginCheck on firm ${firmId}, user ${userId}`, logFormat);

            const finishSessionSetup = () => {
                if (firmId) sessionStorage.setItem('firmId', firmId);
                else sessionStorage.removeItem('firmId');
                if (userId) sessionStorage.setItem('userId', userId);
                else sessionStorage.removeItem('userId');

                localStorage.setItem(locals.AUTH_USER_ID, user_id);
                locals.setArray(`${locals.AUTH_FIRMS}-${userId}`, app_metadata.firms);

                if (!!socket?.connected) {
                    socket.emit('register', {firmId, userId, token: accessToken});
                }

                store.dispatch(MiscActions.loginResult(true, '', firmId, userId));

                const openResult = idb.isOpen();
                if (openResult.isOk()) {
                    if (openResult.unwrap() === false) {
                        const result = idb.open();
                        if (result.isErr()) console.error('🔑 Auth: setSession could not open IDB database (was closed)', result.unwrapErr());
                    }
                }
                else {
                    const result = idb.open(firmId, userId);
                    if (result.isErr()) console.error('🔑 Auth: setSession could not open IDB database (first open)', result.unwrapErr());
                }

                idb.initQueueCount();

                // One-time-ever drain of the autosave store to clear bloat from the prior
                // key-mismatch leak. Gated by a per-(firmId, userId) localStorage flag so it
                // never runs a second time. Fire-and-forget — must not block login.
                if (firmId && userId) {
                    purgeAutosaveOnce(firmId, userId).catch(err =>
                        console.error('🔑 Auth: purgeAutosaveOnce failed', err)
                    );
                }
            }

            if (isPortal) {
                finishSessionSetup();

                polling.addCallback('setSession', fetchFromServer);
                polling.start('setSession').catch(err => console.error('🔑 Auth: Error calling polling start', err));
            }
            else {
                try {
                    const result = await api.verifyLogin(firmId, userId, user_metadata, email);

                    if (result.isErr()) {
                        const err = result.unwrapErr();
                        if (err.status === 503) {
                            finishSessionSetup();
                        }
                        else {
                            console.error(`🔑 Auth: verifyLogin error ${err.status}: ${err.message}`);
                            const loginError = buildLoginError(err);
                            store.dispatch(MiscActions.setConnectionState(false));
                            store.dispatch(MiscActions.loginResult(false, loginError, null, null));
                        }
                    }
                    else {
                        const {subscription, firm} = result.unwrap().body;

                        if (logging) console.debug('%c🔑 Auth: setSession api.loginCheck returned', logFormat, {subscription, firm});

                        if (subscription) {
                            const sub = subscription

                            if (sub.isFullyCancelled) {
                                Alert.add(new Alert(
                                    Alert.WARNING,
                                    firmId,
                                    firm?.addedOn,
                                    `The administrator has cancelled this subscription. Go to Settings/My Account and click on [Manage] your payment methods and contact info`
                                ));

                                store.dispatch(MiscActions.setReadOnly(true));
                            }
                            else if (sub.willCancelAtPeriodEnd) {
                                Alert.add(new Alert(
                                    Alert.WARNING,
                                    firmId,
                                    firm?.addedOn,
                                    `The administrator has cancelled this subscription, the last day of availability will be ${sub.subscriptionEndDate.toLocaleString()}`
                                ));
                            }

                            finishSessionSetup();

                            polling.addCallback('setSession', fetchFromServer);
                            polling.start('setSession').catch(err => console.error(`🔑 Auth: Error calling polling start ${err?.message}`, err));
                        }
                        else {
                            const msg = 'No subscription was found for this user. Please contact Support.';
                            console.error(`🔑 Auth: ${msg} firmId=${firmId}, userId=${userId}`);
                            confirmError('No Subscription', msg);
                            store.dispatch(MiscActions.loginResult(false, msg, null, null));
                        }
                    }
                }
                catch (err) {
                    // Only thrown for truly unexpected errors (not HTTP status errors)
                    console.error(`🔑 Auth: setSession login error ${err?.message}`, err);
                    const loginError = buildLoginError(err);
                    store.dispatch(MiscActions.setConnectionState(false));
                    store.dispatch(MiscActions.loginResult(false, loginError, null, null));
                }
                finally {
                    store.dispatch(MiscActions.setLoadingMessage(''));
                    store.dispatch(MiscActions.updateLoading(false));
                }
            }
        }
        catch (err) {
            console.error(`🔑 Auth: setSession login error ${err?.message}`, err);
            const loginError = buildLoginError(err);
            store.dispatch(MiscActions.setConnectionState(false));
            store.dispatch(MiscActions.loginResult(false, loginError, null, null));
        }
        finally {
            store.dispatch(MiscActions.setLoadingMessage(''));
            store.dispatch(MiscActions.updateLoading(false));

            void pWaitFor(() => Auth.isAuthenticated() && !!getCurrentUser(), {signal: globalAbortSignal, timeout: 20000})
                .then(() => initializeSupport()).catch(() => { /* Offline or cancelled login: support can open later. */ });
        }
    };

    /**
     * Perform logout cleanup (stop polling, unregister socket, clear storage).
     * The actual Auth0 logout redirect is handled by the Auth0 SDK in useLogout.
     */
    static logoutCleanup = (isPortal?: boolean): boolean => {
        stopRecording();
        if (logging) console.debug(`%c🔑 Auth: Logout (portal?: ${isPortal}`, logFormat);

        const status = store.getState().savedStatus;
        const allSaved = status.length === 0;

        if (allSaved) {
            resetSupport();
            polling.stop('logout');

            if (socket && !!socket.connected) {
                socket.emit('unregister', {
                    firmId: getCurrentFirmId(),
                    userId: getCurrentUserId()
                });
            }

            localStorage.removeItem(locals.AUTH_EXPIRES_AT);
            localStorage.removeItem(locals.AUTH_ACCESS_TOKEN);
            localStorage.removeItem(locals.AUTH_ID_TOKEN);

            sessionStorage.removeItem('firmId');
            sessionStorage.removeItem('userId');

            return true;
        }

        return false;
    };
}

export default Auth;
